import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  FiCopy,
  FiLink,
  FiShare2,
  FiX,
  FiDownload,
  FiCode,
  FiLock,
  FiGlobe,
  FiClock,
  FiUsers,
  FiCheck
} from 'react-icons/fi'
import { 
  IoLogoTwitter,
  IoLogoGithub,
  IoLogoLinkedin,
  IoLogoFacebook,
  IoLogoWhatsapp
} from 'react-icons/io5'
import { FaFire } from 'react-icons/fa'
import toast from 'react-hot-toast'

const ShareModal = ({ file, onClose }) => {
  const [link, setLink] = useState('')
  const [isCopied, setIsCopied] = useState(false)
  const [shareType, setShareType] = useState('public')
  const [expiry, setExpiry] = useState('7d')
  const [allowEdit, setAllowEdit] = useState(false)
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (file) {
      const baseUrl = window.location.origin
      const fileId = file.id || 'demo'
      const shareLink = `${baseUrl}/share/${fileId}`
      setLink(shareLink)
    }
  }, [file])

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link)
    setIsCopied(true)
    toast.success('Link copied to clipboard! 📋')
    
    setTimeout(() => setIsCopied(false), 2000)
  }

  const handleDownload = () => {
    const element = document.createElement('a')
    const fileContent = new Blob([file.content], { type: 'text/plain' })
    element.href = URL.createObjectURL(fileContent)
    element.download = file.name
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
    toast.success('File downloaded! 💾')
  }

  const handleShareSocial = (platform) => {
    const text = encodeURIComponent(`Check out this code I wrote on CodeFire: ${file.name}`)
    const url = encodeURIComponent(link)
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      github: `https://github.com/`
    }

    window.open(urls[platform], '_blank')
    toast.success(`Sharing to ${platform}...`)
  }

  const generateEmbedCode = () => {
    return `<iframe src="${link}/embed" width="100%" height="400" frameborder="0" allow="clipboard-write"></iframe>`
  }

  const handleCopyEmbed = () => {
    const embedCode = generateEmbedCode()
    navigator.clipboard.writeText(embedCode)
    toast.success('Embed code copied! 📋')
  }

  const handleGenerateQR = () => {
    toast.success('QR code generated! 📱')
    // QR code generation would go here
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 flex items-center justify-center">
              <FiShare2 className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Share "{file?.name}"
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Share your code with others
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <FiX className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Link Section */}
          <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Shareable Link
              </label>
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <FiLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={link}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
                <button
                  onClick={handleCopyLink}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium ${
                    isCopied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-primary-500 to-red-500 text-white hover:from-primary-600 hover:to-red-600'
                  }`}
                >
                  {isCopied ? <FiCheck /> : <FiCopy />}
                  <span>{isCopied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>

            {/* Share Options */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Share Options
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => handleShareSocial('twitter')}
                  className="flex flex-col items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
                >
                  <IoLogoTwitter className="text-blue-500 text-2xl mb-2" />
                  <span className="text-sm font-medium">Twitter</span>
                </button>
                <button
                  onClick={() => handleShareSocial('facebook')}
                  className="flex flex-col items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
                >
                  <IoLogoFacebook className="text-blue-600 text-2xl mb-2" />
                  <span className="text-sm font-medium">Facebook</span>
                </button>
                <button
                  onClick={() => handleShareSocial('linkedin')}
                  className="flex flex-col items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
                >
                  <IoLogoLinkedin className="text-blue-700 text-2xl mb-2" />
                  <span className="text-sm font-medium">LinkedIn</span>
                </button>
                <button
                  onClick={() => handleShareSocial('whatsapp')}
                  className="flex flex-col items-center justify-center p-4 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-xl transition-colors"
                >
                  <IoLogoWhatsapp className="text-green-500 text-2xl mb-2" />
                  <span className="text-sm font-medium">WhatsApp</span>
                </button>
              </div>
            </div>

            {/* Settings */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Share Settings
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <FiGlobe className="inline mr-2" />
                      Access Type
                    </label>
                    <select
                      value={shareType}
                      onChange={(e) => setShareType(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      <option value="public">Public (Anyone with link)</option>
                      <option value="private">Private (Only you)</option>
                      <option value="password">Password Protected</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <FiClock className="inline mr-2" />
                      Link Expiry
                    </label>
                    <select
                      value={expiry}
                      onChange={(e) => setExpiry(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    >
                      <option value="1h">1 Hour</option>
                      <option value="1d">1 Day</option>
                      <option value="7d">7 Days</option>
                      <option value="30d">30 Days</option>
                      <option value="never">Never</option>
                    </select>
                  </div>
                </div>

                {shareType === 'password' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <FiLock className="inline mr-2" />
                      Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="allowEdit"
                    checked={allowEdit}
                    onChange={(e) => setAllowEdit(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="allowEdit" className="text-sm text-gray-900 dark:text-white">
                    Allow others to edit this file
                  </label>
                </div>
              </div>
            </div>

            {/* Advanced Options */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Advanced Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleCopyEmbed}
                  className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl"
                >
                  <FiCode />
                  <span>Copy Embed Code</span>
                </button>
                <button
                  onClick={handleGenerateQR}
                  className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl"
                >
                  <FiDownload />
                  <span>Generate QR Code</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <FaFire className="text-primary-600 dark:text-primary-400 animate-pulse" />
                <span>Shared via CodeFire</span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg font-medium"
              >
                Download File
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-red-500 text-white hover:from-primary-600 hover:to-red-600 rounded-lg font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default ShareModal